import React, {useEffect, useState, ReactElement} from 'react';
import SftpUsersService, {SftpUser} from '../../../services/sftp-users';
import {useBoolean} from '../../../hooks/useBoolean';
import Table from '../../../components/SimpleTable';
import {Field, Form, Formik} from 'formik';
import FormikTextArea from '../../../components/formik-text-area';
import Label from '../../../../shared/components/label';
import UniversityDropdown from '../UniversityDropdown';
import {fetchSlugForUniversity} from '../../../redux/admin/actions/universities';
import Button from '../../../components/button';
import {errorMessage, flashMessage} from '../../../../shared/actions/notifications';
import {useDispatch} from 'react-redux';
import Modal from 'shared/components/modal';

const columns = [
  {
    value: 'User Name',
  },
  {
    value: 'Home Directory',
  },
  {
    value: 'Actions',
  },
];

const validate = (values: {slug: string; publicKey: string}): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!values.slug) {
    errors.slug = 'College must be selected';
  }

  return errors;
};

const SftpUsers = () => {
  const loading = useBoolean(true);
  const [sftpUsers, setSftpUsers] = useState<SftpUser[]>([]);
  const [universityId, setUniversityId] = useState<string | null>(null);
  const dispatch = useDispatch();
  const [instructionsSlug, setInstructionsSlug] = useState<string | null>(null);

  useEffect(() => {
    SftpUsersService.index().then(setSftpUsers).then(loading.setFalse);
  }, [loading.setFalse]);

  const rows = sftpUsers
    .map(({userName, homeDirectory}) => [
      {value: userName},
      {value: homeDirectory},
      {value: <Button onClick={() => setInstructionsSlug(userName)}>View Instructions</Button>},
    ])
    .sort((a, b) => `${a[0].value}`.localeCompare(`${b[0].value}`));

  const closeModal = () => setInstructionsSlug(null);

  return (
    <div className="mb-36">
      <Modal show={!!instructionsSlug} onClose={closeModal}>
        <Modal.TextHeader onClose={closeModal}>
          Instructions for {instructionsSlug}
        </Modal.TextHeader>
        <Modal.Body>
          <p>Your username is: {instructionsSlug}</p>
          <p>The server name is: sftp.raise.me</p>
          <p>Your home directory is /production-raiseme-sftp/partners/{instructionsSlug}</p>
          <p>
            Under your home directory, there is a from-raiseme directory and a to-raiseme directory,
            so that it’s clear which direction we all intend the information to flow.
          </p>
        </Modal.Body>
        <Modal.Footer
          primaryButton={{
            text: 'Copy Instructions',
            onClick: (): void => {
              navigator.clipboard
                .writeText(
                  `Your username is ${instructionsSlug}\n\nThe server name is sftp.raise.me\n\nYour home directory is /production-raiseme-sftp/partners/${instructionsSlug}\n\nUnder your home directory, there is a from-raiseme directory and a to-raiseme directory, so that it’s clear which direction we all intend the information to flow.`
                )
                .then(() => {
                  dispatch(flashMessage('Instructions copied to clipboard!'));
                });
            },
          }}
          secondaryButton={{
            text: 'Close',
            onClick: closeModal,
          }}
        />
      </Modal>
      <h2 className="shared-font title">SFTP Users</h2>
      <Formik
        validate={validate}
        initialValues={{slug: '', publicKey: ''}}
        onSubmit={(values, {resetForm}) => {
          console.log(values);
          SftpUsersService.create(values)
            .then(() => {
              setSftpUsers([
                ...sftpUsers,
                {userName: values.slug, homeDirectory: `/partners/${values.slug}`},
              ]);
              dispatch(flashMessage(`User ${values.slug} added successfully!`));
              resetForm();
            })
            .catch((error) => {
              dispatch(errorMessage(error.message, 10_000));
            });
        }}
      >
        {() => (
          <Form className="mb-20">
            <Label>
              College
              <Field name="slug">
                {({form: {setFieldValue}}): ReactElement => (
                  <UniversityDropdown
                    autoSelectFirstUniversity
                    value={universityId}
                    onChange={(id): void => {
                      setUniversityId(id);
                      fetchSlugForUniversity(id).then((slug) => setFieldValue('slug', slug));
                    }}
                  />
                )}
              </Field>
            </Label>
            <Label>
              Public Key
              <FormikTextArea name="publicKey" placeholder="Paste your public key here" />
            </Label>
            <Button type="submit">Add User</Button>
          </Form>
        )}
      </Formik>
      <Table caption="Existing Users">
        <Table.Header columns={columns} />
        <Table.Body
          rows={rows}
          emptyState={{
            numberOfColumns: columns.length,
            text: loading.value ? 'Loading...' : 'No users yet.',
          }}
        />
      </Table>
    </div>
  );
};

export default SftpUsers;
